/**
 * VSElectric — Animated lightning VS badge for starter matchups.
 * Uses canvas for animated lightning bolts around a glowing "VS" text.
 *
 * Usage:
 *   <VSElectric size="sm" />   // 56px badge — for section headers
 *   <VSElectric size="md" />   // 110px badge
 */

import { type ReactElement, useEffect, useRef } from 'react';

// =============================================================================
// Types
// =============================================================================

interface VSElectricProps {
  size?: 'sm' | 'md' | 'lg';
}

interface SizeConfig {
  badge: number;
  font: string;
  orb: number;
}

interface Bolt {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  life: number;
  maxLife: number;
  roughness: number;
  color: string;
  alpha: number;
}

// =============================================================================
// Config
// =============================================================================

const SIZES: Record<string, SizeConfig> = {
  sm: { badge: 56, font: '0.85rem', orb: 30 },
  md: { badge: 110, font: '1.6rem', orb: 60 },
  lg: { badge: 160, font: '2.4rem', orb: 90 },
};

const BOLT_COLOR_PRIMARY = '#ffe566';
const BOLT_COLOR_SECONDARY = '#ffffff';
const FPS = 20;
const INTERVAL = 1000 / FPS;

// =============================================================================
// Lightning drawing
// =============================================================================

function drawLightning(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  roughness: number,
  branches: boolean,
  color: string,
  alpha: number,
) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.shadowColor = color;
  ctx.shadowBlur = 8;
  ctx.beginPath();

  function bolt(ax: number, ay: number, bx: number, by: number, depth: number) {
    if (depth === 0) {
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      return;
    }
    const mx = (ax + bx) / 2 + (Math.random() - 0.5) * roughness * depth;
    const my = (ay + by) / 2 + (Math.random() - 0.5) * roughness * depth;
    bolt(ax, ay, mx, my, depth - 1);
    bolt(mx, my, bx, by, depth - 1);
    if (branches && depth === 2 && Math.random() > 0.5) {
      const bx2 = mx + (Math.random() - 0.5) * roughness * 3;
      const by2 = my + (Math.random() - 0.5) * roughness * 3;
      ctx.globalAlpha = alpha * 0.5;
      ctx.lineWidth = 0.6;
      bolt(mx, my, bx2, by2, depth - 1);
      ctx.globalAlpha = alpha;
      ctx.lineWidth = 1.2;
    }
  }

  bolt(x1, y1, x2, y2, 4);
  ctx.stroke();
  ctx.restore();
}

// =============================================================================
// Component
// =============================================================================

export function VSElectric({ size = 'md' }: VSElectricProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const s = SIZES[size] ?? SIZES.md;
  const W = s.badge + 60;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = W;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cx = W / 2;
    const cy = W / 2;
    const r = s.badge / 2;

    let frame = 0;
    let bolts: Bolt[] = [];
    let nextBolt = 0;
    let lastTime = 0;

    function randomEdgePoint() {
      const angle = Math.random() * Math.PI * 2;
      const dist = r * (0.7 + Math.random() * 0.5);
      return { x: cx + Math.cos(angle) * dist, y: cy + Math.sin(angle) * dist };
    }

    function spawnBolt(): Bolt {
      const a = randomEdgePoint();
      const b = randomEdgePoint();
      return {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        life: 0,
        maxLife: 18 + Math.floor(Math.random() * 14),
        roughness: 10 + Math.random() * 14,
        color: Math.random() > 0.4 ? BOLT_COLOR_PRIMARY : BOLT_COLOR_SECONDARY,
        alpha: 0.6 + Math.random() * 0.4,
      };
    }

    function tick(timestamp: number) {
      rafRef.current = requestAnimationFrame(tick);
      if (timestamp - lastTime < INTERVAL) return;
      lastTime = timestamp;
      ctx!.clearRect(0, 0, W, W);
      frame++;

      if (frame >= nextBolt) {
        const count = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < count; i++) bolts.push(spawnBolt());
        nextBolt = frame + 20 + Math.floor(Math.random() * 30);
      }

      bolts = bolts.filter((b) => b.life < b.maxLife);
      for (const b of bolts) {
        const fade = 1 - b.life / b.maxLife;
        drawLightning(ctx!, b.x1, b.y1, b.x2, b.y2, b.roughness, true, b.color, b.alpha * fade);
        b.life++;
      }

      if (frame % 90 === 0) {
        drawLightning(
          ctx!,
          cx + (Math.random() - 0.5) * 20,
          cy - r,
          cx + (Math.random() - 0.5) * 20,
          cy + r,
          18,
          true,
          '#fff',
          0.9,
        );
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [W, s.badge]);

  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      style={{ width: s.badge, height: s.badge }}
    >
      {/* Glow orb */}
      <div
        className="absolute rounded-full animate-pulse"
        style={{
          width: s.orb,
          height: s.orb,
          background:
            'radial-gradient(circle, rgba(255,221,0,0.53) 0%, rgba(255,153,0,0.2) 40%, transparent 70%)',
        }}
      />
      {/* Ring glow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow:
            '0 0 0 1px rgba(255,204,0,0.2), 0 0 16px rgba(255,204,0,0.27), inset 0 0 16px rgba(255,204,0,0.13)',
        }}
      />
      {/* Lightning canvas */}
      <canvas
        ref={canvasRef}
        className="absolute pointer-events-none"
        style={{ top: -30, left: -30, width: W, height: W, zIndex: 5 }}
      />
      {/* VS text */}
      <span
        className="relative z-10 font-extrabold tracking-widest text-white"
        style={{
          fontSize: s.font,
          textShadow: '0 0 6px #fff, 0 0 14px #ffe066, 0 0 28px #ffcc00, 0 0 50px #ff9900',
        }}
      >
        VS
      </span>
    </div>
  );
}
